import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './lib/contexts/AuthContext';
import UserLayout from './components/UserLayout';
import AdminLayout from './components/AdminLayout';

import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import WalletPage from './pages/WalletPage';
import InvestmentsPage from './pages/InvestmentsPage';
import PlansPage from './pages/PlansPage';
import TeamPage from './pages/TeamPage';
import EarningsPage from './pages/EarningsPage';
import DepositPage from './pages/DepositPage';
import WithdrawPage from './pages/WithdrawPage';
import KycPage from './pages/KycPage';
import ProfilePage from './pages/ProfilePage';
import NotificationsPage from './pages/NotificationsPage';

import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminWithdrawalsPage from './pages/admin/AdminWithdrawalsPage';
import AdminDepositsPage from './pages/admin/AdminDepositsPage';
import AdminKycPage from './pages/admin/AdminKycPage';
import AdminPlansPage from './pages/admin/AdminPlansPage';
import AdminPlanWizardPage from './pages/admin/AdminPlanWizardPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminBusinessRulesPage from './pages/admin/AdminBusinessRulesPage';
import AdminTeamPage from './pages/admin/AdminTeamPage';
import AdminReportsPage from './pages/admin/AdminReportsPage';
import AdminTransactionsPage from './pages/admin/AdminTransactionsPage';
import AdminCommissionRulesPage from './pages/admin/AdminCommissionRulesPage';
import AdminLevelRulesPage from './pages/admin/AdminLevelRulesPage';
import AdminBonusesPage from './pages/admin/AdminBonusesPage';
import AdminInvestmentsPage from './pages/admin/AdminInvestmentsPage';
import AdminWorkingDaysPage from './pages/admin/AdminWorkingDaysPage';
import AdminPaymentMethodsPage from './pages/admin/AdminPaymentMethodsPage';
import AdminNotificationsPage from './pages/admin/AdminNotificationsPage';
import AdminSupportPage from './pages/admin/AdminSupportPage';
import AdminBonanzaRanksPage from './pages/admin/AdminBonanzaRanksPage';
import AdminAwardsPage from './pages/admin/AdminAwardsPage';
import AdminLevelUnlockPage from './pages/admin/AdminLevelUnlockPage';
import AdminAuditLogsPage from './pages/admin/AdminAuditLogsPage';
import AdminAdminsPage from './pages/admin/AdminAdminsPage';
import AdminAnalyticsPage from './pages/admin/AdminAnalyticsPage';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <div className="flex items-center justify-center h-screen bg-fintech-900"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" /></div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <div className="flex items-center justify-center h-screen bg-fintech-900"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" /></div>;
  const isAdmin = user?.permissions?.some(p => p && p.startsWith('ADMIN_'));
  if (!isAdmin) return <Navigate to="/dashboard" />;
  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            <Route path="/" element={<ProtectedRoute><UserLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/dashboard" />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="wallet" element={<WalletPage />} />
              <Route path="investments" element={<InvestmentsPage />} />
              <Route path="investments/plans" element={<PlansPage />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="earnings" element={<EarningsPage />} />
              <Route path="deposit" element={<DepositPage />} />
              <Route path="withdraw" element={<WithdrawPage />} />
              <Route path="kyc" element={<KycPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="notifications" element={<NotificationsPage />} />
            </Route>

            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index element={<AdminDashboardPage />} />
              <Route path="analytics" element={<AdminAnalyticsPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="kyc" element={<AdminKycPage />} />
              <Route path="team" element={<AdminTeamPage />} />
              <Route path="deposits" element={<AdminDepositsPage />} />
              <Route path="withdrawals" element={<AdminWithdrawalsPage />} />
              <Route path="transactions" element={<AdminTransactionsPage />} />
              <Route path="commission-rules" element={<AdminCommissionRulesPage />} />
              <Route path="level-rules" element={<AdminLevelRulesPage />} />
              <Route path="bonuses" element={<AdminBonusesPage />} />
              <Route path="bonanza-ranks" element={<AdminBonanzaRanksPage />} />
              <Route path="awards" element={<AdminAwardsPage />} />
              <Route path="level-unlock" element={<AdminLevelUnlockPage />} />
              <Route path="plans" element={<AdminPlansPage />} />
              <Route path="plans/new" element={<AdminPlanWizardPage />} />
              <Route path="plans/:id/edit" element={<AdminPlanWizardPage />} />
              <Route path="investments" element={<AdminInvestmentsPage />} />
              <Route path="working-days" element={<AdminWorkingDaysPage />} />
              <Route path="payment-methods" element={<AdminPaymentMethodsPage />} />
              <Route path="reports" element={<AdminReportsPage />} />
              <Route path="notifications" element={<AdminNotificationsPage />} />
              <Route path="support" element={<AdminSupportPage />} />
              <Route path="audit-logs" element={<AdminAuditLogsPage />} />
              <Route path="business-rules" element={<AdminBusinessRulesPage />} />
              <Route path="settings" element={<AdminSettingsPage />} />
              <Route path="admins" element={<AdminAdminsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
